import { PlusOutlined } from '@ant-design/icons';
import { Dayjs } from '@mst-fe/shared/dayjs';
import { Button, Col, DatePicker, Form, FormInstance, Input, Select } from 'antd';
import { useEffect, useMemo } from 'react';

import classes from './ReferenceDataFormFields.module.css';

import {
  AMERICAN_DATE_FORMAT,
  FORM_LAYOUT_CONTROL_OFFSET,
  FORM_LAYOUT_LABEL,
  MODAL_FORM_LAYOUT_CONTROL_OFFSET,
  MODAL_FORM_LAYOUT_LABEL,
} from '../../utils/midas-constants';
import { MIN_SEARCH_CRITERIA_WITH_DATE, mapFieldToInputType } from '../_shared/advanced-search-constants';
import { AdvancedProductSearchCriteria, AdvancedProductSearchResultHeader, AdvancedProductSearchSource } from './AdvancedProductSearchForm';

type ReferenceDataFormFieldsParams = {
  formInstance: FormInstance;
  sourcesList: AdvancedProductSearchSource[];
  dataSource?: string;
  resultHeaders: AdvancedProductSearchResultHeader;
  setDataSource: (value: string | undefined) => void;
  setSearchCriteria: (searchCriteria: AdvancedProductSearchCriteria[]) => void;
  embedded: boolean;
};

type FormReferenceDataFilter = { fieldName: string | undefined; value: string | number | Dayjs | undefined };

export default function ReferenceDataFormFields({
  formInstance,
  sourcesList,
  dataSource,
  resultHeaders,
  setDataSource,
  setSearchCriteria,
  embedded,
}: ReferenceDataFormFieldsParams) {
  const referenceDataSource = Form.useWatch<string | undefined>('referenceDataSource', formInstance);
  const referenceDataDate = Form.useWatch<Dayjs | undefined>('referenceDataDate', formInstance);
  const referenceDataFilters = Form.useWatch<(FormReferenceDataFilter | undefined)[] | undefined>('referenceDataFilters', formInstance);

  const dataSourceHeadersMap = useMemo(() => {
    if (!dataSource || !resultHeaders[dataSource]) {
      return undefined;
    }
    return new Map(Object.entries(resultHeaders[dataSource]));
  }, [resultHeaders, dataSource]);

  // compute the search criteria
  useEffect(() => {
    if (
      !dataSourceHeadersMap ||
      !referenceDataDate ||
      !referenceDataFilters ||
      referenceDataFilters.some((f) => !f || !f.fieldName || !f.value)
    ) {
      return;
    }

    setSearchCriteria([
      {
        field: 'date',
        value: referenceDataDate,
      },
      ...(referenceDataFilters as FormReferenceDataFilter[]).map(({ fieldName, value }) => ({
        field: fieldName!,
        value: value!,
      })),
    ]);
  }, [referenceDataDate, dataSourceHeadersMap, referenceDataFilters, setSearchCriteria]);

  const getFilterValueType = (fieldName: string | undefined) => {
    if (!fieldName || !dataSourceHeadersMap) {
      return 'text';
    }

    const selectedFieldType = dataSourceHeadersMap.get(fieldName);
    return selectedFieldType ? mapFieldToInputType(fieldName, selectedFieldType) : 'text';
  };

  const wrapperCol = useMemo(() => (embedded ? FORM_LAYOUT_CONTROL_OFFSET : MODAL_FORM_LAYOUT_CONTROL_OFFSET), [embedded]);

  const remainingSearchCriteriaOptions = useMemo(() => {
    const selectedFields = (referenceDataFilters ?? []).map((f) => f?.fieldName).filter((n): n is string => !!n);
    const columnNames = dataSource && resultHeaders[dataSource] ? Object.keys(resultHeaders[dataSource]) : undefined;
    return columnNames
      ?.filter((name) => {
        return !(name.toLowerCase() === 'date');
      })
      .map((name) => ({
        disabled: selectedFields.includes(name),
        label: name,
        value: name,
      }));
  }, [resultHeaders, dataSource, referenceDataFilters]);

  useEffect(() => setDataSource(referenceDataSource), [referenceDataSource]);

  const onDataSourceChange = () => formInstance.resetFields(['referenceDataFilters']);

  return (
    <>
      <Form.Item
        label="Data Source"
        labelAlign="left"
        name="referenceDataSource"
        data-testid="data-query-search-reference-data-source"
        rules={[{ required: true, message: 'Select a data source!' }]}
      >
        <Select
          loading={!sourcesList.length}
          onChange={onDataSourceChange}
          disabled={!sourcesList.length}
          placeholder="Select a data source"
        >
          {sourcesList.map(({ type, displayName }) => (
            <Select.Option key={type} value={type}>
              {displayName}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      <Form.Item rules={[{ required: true, message: 'Enter a search date' }]} name="referenceDataDate" label="Date" labelAlign="left">
        <DatePicker format={AMERICAN_DATE_FORMAT} aria-label="Date picker" />
      </Form.Item>

      {referenceDataSource && (
        <Form.List name="referenceDataFilters">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={`reference-data-filter-${key}`} className={classes.searchCriteriaFormGroup}>
                  {/* Filter label */}
                  <Col {...(embedded ? FORM_LAYOUT_LABEL : MODAL_FORM_LAYOUT_LABEL)} style={{ padding: 0 }}>
                    <label htmlFor={`reference-data-filter-${key}`}>
                      {name < MIN_SEARCH_CRITERIA_WITH_DATE ? <span className={classes.starForRequired}>*</span> : <></>}
                      Filter {name}
                    </label>
                  </Col>

                  {/* Filter field name */}
                  <Form.Item {...restField} name={[name, 'fieldName']} rules={[{ required: true, message: 'Enter a key here' }]}>
                    <Select options={remainingSearchCriteriaOptions} placeholder="Select a field" aria-label="Select a field" />
                  </Form.Item>

                  {/* Filter value */}
                  <Form.Item {...restField} name={[name, 'value']} rules={[{ required: true, message: 'Enter a value here' }]}>
                    <Input
                      placeholder="Enter a search value"
                      type={getFilterValueType(formInstance.getFieldValue(['referenceDataFilters', name, 'fieldName']))}
                    />
                  </Form.Item>

                  <Button
                    disabled={key < MIN_SEARCH_CRITERIA_WITH_DATE}
                    className={key < MIN_SEARCH_CRITERIA_WITH_DATE ? classes.invisibleRemove : ''}
                    type="link"
                    onClick={() => remove(name)}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Form.Item wrapperCol={wrapperCol}>
                <Button
                  disabled={(referenceDataFilters?.length ?? 0) >= (remainingSearchCriteriaOptions?.length ?? 0)}
                  icon={<PlusOutlined />}
                  onClick={() => add()}
                >
                  Add Filter
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      )}
    </>
  );
}
